// src/pages/RoutingXPOs.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { AlertCircle, Download, ListFilter, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { getCurrentUser } from "../utils/auth";
import { usePermissions } from "../utils/permissions";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import {
  calculateAllocationStats,
  ELIGIBLE_PO_QTY_DIAGNOSTIC_KEY,
} from "../utils/routingXPosAllocation";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

const EMPTY_DATASET = {
  headers: [],
  data: [],
  checks: {
    rowViolations: [],
    poViolations: [],
    eligibleQtyDiagnostics: [],
  },
};

const ROW_ID_KEY = "__rowId";
const ROUTING_ROW_ID_KEY = "__routingRowId";
const STICKY_LABELS = ["PO Number", "Load ID", "RT Status"];
const STICKY_COLUMN_WIDTHS = {
  "PO Number": 120,
  "Load ID": 100,
  "RT Status": 110,
};
const DEFAULT_STICKY_WIDTH = 128;
const DATE_KEYS_WITH_TIME = new Set(["firstupdatedt", "lastupdatedt"]);
const RENAME_LABELS = {
  "Total_VNPK_qty_P_per_PO": "TTL PO QTY",
  "RowAllocSum": "TTL ALLOC",
};
const PO_MISMATCH_FLAG_KEY = "__po_match";
const ITEM_KEY_PREFIX = "item-";
const ALLOC_KEY_PREFIX = "alloc-";
const DEFAULT_VISIBLE_LABELS = [
  "Outbound Code",
  "PO Number",
  "Load ID",
  "RT Status",
  "Cust Code",
  "MABD",
  "Carrier PU Date",
  "Ship Point",
  "Cases",
  "Pallets",
  "iLS PO",
  "PD Status",
  "Create date",
  "TTL PO QTY",
  "Eligible PO QTY",
  "TTL ALLOC",
  "Routing_vs_PO",
  "Total Cases",
];
const HIDDEN_ROUTING_LABELS = new Set();

const SOURCE_HEADER_CLASS = {
  routing: "bg-amber-50",
  po: "bg-emerald-50",
  alloc: "bg-lime-50",
  meta: "bg-slate-100",
};

const numberFormatter = new Intl.NumberFormat();
const DATE_LABEL_PATTERN = /(date|eta|etd|mabd|dns|window|delivery|carrier|create)/i;


const ensureDatasetShape = (payload = {}) => {
  const headers = Array.isArray(payload.headers) ? payload.headers.map((header) => {
    if (header?.label && RENAME_LABELS[header.label]) {
      return { ...header, label: RENAME_LABELS[header.label] };
    }
    return header;
  }) : [];
  const data = Array.isArray(payload.data) ? payload.data : [];
  const checks = typeof payload.checks === "object" && payload.checks !== null ? payload.checks : {};
  const custKey = getHeaderKey(headers, "Cust Code");
  const poKey = getHeaderKey(headers, "PO#") || getHeaderKey(headers, "PO Number");
  const loadKey = getHeaderKey(headers, "Load ID");
  const withIds = data.map((row, index) => {
    if (row && row[ROW_ID_KEY]) return row;
    const next = { ...(row || {}) };
    const normalize = (value) => (value === undefined || value === null ? "" : String(value).trim());
    const cust = normalize(next[custKey]).toUpperCase();
    const po = normalize(next[poKey]);
    const load = normalize(next[loadKey]);
    next[ROW_ID_KEY] = `${cust}|${po}|${load}|${index}`;
    return next;
  });
  return {
    headers,
    data: withIds,
    checks: {
      rowViolations: Array.isArray(checks.rowViolations) ? checks.rowViolations : [],
      poViolations: Array.isArray(checks.poViolations) ? checks.poViolations : [],
      eligibleQtyDiagnostics: Array.isArray(checks.eligibleQtyDiagnostics)
        ? checks.eligibleQtyDiagnostics
        : [],
    },
  };
};

const getHeaderKey = (headers, label) => headers.find((header) => header.label === label)?.key || null;

const reorderRoutingHeaders = (headers = []) => {
  if (!Array.isArray(headers) || !headers.length) return headers;
  const tailOrder = ["TTL ALLOC", "Routing_vs_PO", "Total Cases"];
  const tailSet = new Set(tailOrder);
  const leading = headers.filter((header) => !tailSet.has(header.label));
  const trailing = tailOrder
    .map((label) => headers.find((header) => header.label === label))
    .filter(Boolean);
  return [...leading, ...trailing];
};

const shouldFormatAsDate = (header) => {
  if (!header) return false;
  const key = (header.key || "").toLowerCase();
  if (DATE_KEYS_WITH_TIME.has(key)) return true;
  const label = header.label || "";
  return DATE_LABEL_PATTERN.test(label);
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return numberFormatter.format(numeric);
};

const formatDate = (value, headerKey = "") => {
  if (!value) return "";
  const text = String(value).trim();
  if (!text) return "";
  const asDate = Date.parse(text);
  if (Number.isNaN(asDate)) return text;
  const date = new Date(asDate);
  const key = (headerKey || "").toLowerCase();
  if (DATE_KEYS_WITH_TIME.has(key)) {
    return date.toLocaleString();
  }
  return date.toLocaleDateString("en-US");
};

export default function RoutingXPOs() {
  const me = getCurrentUser();
  const [dataset, setDataset] = useState(EMPTY_DATASET);
  const [loading, setLoading] = useState(true);
  const [actionPending, setActionPending] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    custCode: "",
    rtStatus: "",
    pdStatus: "",
  });
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const headerCheckboxRef = useRef(null);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(new Set());
  const [columnFilterOpen, setColumnFilterOpen] = useState(false);
  const columnFilterRef = useRef(null);
  const [ilsPreview, setIlsPreview] = useState(null);
  const [ilsModalOpen, setIlsModalOpen] = useState(false);
  const [ilsPreviewLoading, setIlsPreviewLoading] = useState(false);
  const [ilsPostMessage, setIlsPostMessage] = useState("");
  const [postingIls, setPostingIls] = useState(false);
  const [ilsPostSummary, setIlsPostSummary] = useState(null);
  const [pendingEdits, setPendingEdits] = useState({});
  const [savingEdits, setSavingEdits] = useState(false);
  const [itemsByRoutingRowId, setItemsByRoutingRowId] = useState({});
  const [allocEditorPoKey, setAllocEditorPoKey] = useState("");
  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({
      search: "",
      custCode: "",
      rtStatus: "",
      pdStatus: "",
    });
    setSelectedRows(new Set());
    setPage(1);
  }, []);

  const userHeaders = useMemo(() => {
    if (!me?.email) return {};
    return {
      "X-User-Email": me.email,
      "X-User-Role": me.role || "",
    };
  }, [me?.email, me?.role]);
  const { getLevel } = usePermissions();
  const permissionLevel = useMemo(() => getLevel(me?.role, "routing-x-pos"), [getLevel, me?.role]);
  const canEditAlloc = permissionLevel === "Full";

  const getRoutingRowRef = useCallback((row) => {
    if (!row || typeof row !== "object") return "";
    const raw = row[ROUTING_ROW_ID_KEY] ?? row.routing_row_id ?? row.id;
    return raw === undefined || raw === null ? "" : String(raw).trim();
  }, []);
  const getItemsForRow = useCallback(
    (row) => {
      const ref = getRoutingRowRef(row);
      const list = ref ? itemsByRoutingRowId[ref] : null;
      return Array.isArray(list) ? list : [];
    },
    [getRoutingRowRef, itemsByRoutingRowId]
  );
  const displayableHeaders = useMemo(() => {
    if (!dataset.headers.length) return [];
    return dataset.headers.filter((header) => {
      if (HIDDEN_ROUTING_LABELS.has(header.label)) return false;
      const key = String(header?.key || "");
      return !(key.startsWith(ITEM_KEY_PREFIX) || key.startsWith(ALLOC_KEY_PREFIX));
    });
  }, [dataset.headers]);
  const visibleHeaders = useMemo(() => {
    if (!displayableHeaders.length) return [];
    if (!visibleColumnKeys.size) return displayableHeaders;
    return displayableHeaders.filter((header) => visibleColumnKeys.has(header.key));
  }, [displayableHeaders, visibleColumnKeys]);
  const orderedVisibleHeaders = useMemo(
    () => reorderRoutingHeaders(visibleHeaders),
    [visibleHeaders]
  );

  const keyMap = useMemo(() => {
    const headers = dataset.headers || [];
    return {
      cust: getHeaderKey(headers, "Cust Code"),
      rtStatus: getHeaderKey(headers, "RT Status"),
      pdStatus: getHeaderKey(headers, "PD Status"),
      poNumber: getHeaderKey(headers, "PO Number"),
      poSharp: getHeaderKey(headers, "PO#"),
      loadId: getHeaderKey(headers, "Load ID"),
      ils: getHeaderKey(headers, "iLS PO"),
      cases: getHeaderKey(headers, "Cases"),
      rowAllocSum: getHeaderKey(headers, "RowAllocSum") || getHeaderKey(headers, "TTL ALLOC"),
      routingVsPo: getHeaderKey(headers, "Routing_vs_PO"),
      totalCases: getHeaderKey(headers, "Total Cases"),
      totalPerPo: getHeaderKey(headers, "TTL PO QTY") || getHeaderKey(headers, "Total_VNPK_qty_P_per_PO"),
      eligiblePerPo: getHeaderKey(headers, "Eligible PO QTY"),
      outboundCode: getHeaderKey(headers, "Outbound Code"),
    };
  }, [dataset.headers]);
  const rowAllocSumKey = keyMap.rowAllocSum;
  const custKey = keyMap.cust;
  const poNumberKey = keyMap.poNumber;
  const poSharpKey = keyMap.poSharp;
  const loadIdKey = keyMap.loadId;
  const outboundCodeKey = keyMap.outboundCode;
  const hasOutboundCode = useCallback(
    (row) => {
      if (!outboundCodeKey) return false;
      const raw = row?.[outboundCodeKey];
      if (raw === undefined || raw === null) return false;
      return String(raw).trim() !== "";
    },
    [outboundCodeKey],
  );
  const parseAllocValue = useCallback((value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number" && Number.isFinite(value)) {
      return Math.max(0, Math.trunc(value));
    }
    const cleaned = String(value).replace(/,/g, "").trim();
    if (!cleaned) return 0;
    const numeric = Number(cleaned);
    if (Number.isNaN(numeric)) return 0;
    return Math.max(0, Math.trunc(numeric));
  }, []);
  const getPoGroupKey = useCallback(
    (row) => {
      if (!row || typeof row !== "object") return "";
      const custCode = (row[custKey] || "").toString().trim().toUpperCase();
      const poValue = (row[poNumberKey] || row[poSharpKey] || "").toString().trim();
      if (!custCode || !poValue) return "";
      return `${custCode}|${poValue}`;
    },
    [custKey, poNumberKey, poSharpKey]
  );

  useEffect(() => {
    setPendingEdits({});
  }, [dataset]);

  const datasetRowMap = useMemo(() => {
    const map = new Map();
    (dataset.data || []).forEach((row) => {
      const rowKey = row[ROW_ID_KEY];
      if (rowKey) {
        map.set(rowKey, row);
      }
    });
    return map;
  }, [dataset.data]);
  const hasPendingEdits = Object.keys(pendingEdits).length > 0;
  const allocationStats = useMemo(
    () =>
      calculateAllocationStats({
        dataset,
        pendingEdits,
        getItemsForRow,
        parseAllocValue,
        custKey,
        poNumberKey,
        poSharpKey,
        eligibleKey: keyMap.eligiblePerPo,
        rowIdKey: ROW_ID_KEY,
      }),
    [
      dataset,
      pendingEdits,
      getItemsForRow,
      parseAllocValue,
      custKey,
      poNumberKey,
      poSharpKey,
      keyMap.eligiblePerPo,
    ]
  );
  const disableSaveAllocations =
    !canEditAlloc || !hasPendingEdits || savingEdits || allocationStats.hasBlockingMismatch;
  const saveButtonTitle = !canEditAlloc
    ? "You do not have permission to edit allocations."
    : !hasPendingEdits
      ? "No pending edits to save."
          : allocationStats.hasBlockingMismatch
            ? "Resolve the highlighted Eligible PO QTY issue before saving."
            : savingEdits
          ? "Saving allocations..."
          : "Save allocation changes";
  const hasAnyAllocationMismatch =
    allocationStats.rowMismatch.size > 0 || allocationStats.poMismatch.size > 0;
  const selectionColumnWidth = 41;
  const stickyMeta = useMemo(() => {
    let offset = selectionColumnWidth;
    const map = {};
    orderedVisibleHeaders.forEach((header) => {
      if (STICKY_LABELS.includes(header.label)) {
        const width = STICKY_COLUMN_WIDTHS[header.label] || DEFAULT_STICKY_WIDTH;
        map[header.key] = { left: offset, width };
        offset += width;
      }
    });
    return map;
  }, [orderedVisibleHeaders, selectionColumnWidth]);
  const selectedRecords = useMemo(
    () => dataset.data.filter((row) => selectedRows.has(row[ROW_ID_KEY])),
    [dataset.data, selectedRows]
  );
  const selectionMeta = useMemo(() => {
    const custSet = new Set();
    let hasMismatch = false;
    let hasBlankCust = false;
    selectedRecords.forEach((row) => {
      if (row[PO_MISMATCH_FLAG_KEY] === false) {
        hasMismatch = true;
      }
      if (custKey) {
        const value = (row[custKey] ?? "").toString().trim().toUpperCase();
        if (value) custSet.add(value);
        else hasBlankCust = true;
      }
    });
    return { custSet, hasMismatch, hasBlankCust };
  }, [selectedRecords, custKey]);
  const allocationEditorRows = useMemo(() => {
    if (!allocEditorPoKey) return [];
    return (dataset.data || []).filter((row) => getPoGroupKey(row) === allocEditorPoKey);
  }, [allocEditorPoKey, dataset.data, getPoGroupKey]);
  const allocationEditorPoLabel = useMemo(() => {
    if (!allocationEditorRows.length) return "";
    const firstRow = allocationEditorRows[0] || {};
    return (firstRow[poNumberKey] || firstRow[poSharpKey] || "").toString().trim();
  }, [allocationEditorRows, poNumberKey, poSharpKey]);
  const allocationEditorEntries = useMemo(() => {
    const entries = allocationEditorRows.map((row) => {
      const rowKey = row[ROW_ID_KEY];
      const loadId = (row[loadIdKey] || "").toString().trim();
      const rowPosted = hasOutboundCode(row);
      const baseItems = getItemsForRow(row);
      const overrides = rowKey ? pendingEdits[rowKey] || {} : {};
      const items = baseItems.map((item) => {
        const sku = (item?.sku || "").toString().trim();
        const poQty = parseAllocValue(item?.poQty);
        const originalAlloc = parseAllocValue(item?.allocQty);
        const hasOverride = Object.prototype.hasOwnProperty.call(overrides, sku);
        return {
          sku,
          poQty,
          allocQty: hasOverride ? parseAllocValue(overrides[sku]) : originalAlloc,
          inputValue: hasOverride ? String(overrides[sku]) : String(originalAlloc),
        };
      });
      Object.entries(overrides).forEach(([sku, value]) => {
        const token = (sku || "").toString().trim();
        if (!token) return;
        if (items.some((rowItem) => rowItem.sku === token)) return;
        items.push({
          sku: token,
          poQty: 0,
          allocQty: parseAllocValue(value),
          inputValue: String(value ?? ""),
        });
      });
      items.sort((a, b) => a.sku.localeCompare(b.sku));
      return { row, rowKey, loadId, rowPosted, items };
    });
    entries.sort((a, b) => a.loadId.localeCompare(b.loadId) || String(a.rowKey || "").localeCompare(String(b.rowKey || "")));
    return entries;
  }, [allocationEditorRows, loadIdKey, hasOutboundCode, getItemsForRow, pendingEdits, parseAllocValue]);
  const allocationEditorHasEditableRows = useMemo(
    () => allocationEditorEntries.some((entry) => !entry.rowPosted),
    [allocationEditorEntries]
  );
  useEffect(() => {
    if (!allocEditorPoKey) return;
    const exists = (dataset.data || []).some((row) => getPoGroupKey(row) === allocEditorPoKey);
    if (!exists) {
      setAllocEditorPoKey("");
    }
  }, [allocEditorPoKey, dataset.data, getPoGroupKey]);
  const ilsButtonInfo = useMemo(() => {
    if (ilsPreviewLoading) {
      return { disabled: true, title: "Preparing preview..." };
    }
    if (hasPendingEdits) {
      return { disabled: true, title: "Save allocation changes before posting iLS Outbound." };
    }
    if (!selectedRecords.length) {
      return { disabled: true, title: "Select rows to build iLS Outbound preview." };
    }
    if (selectionMeta.hasMismatch) {
      return { disabled: true, title: "Remove rows flagged as PO mismatch before posting." };
    }
    if (selectionMeta.custSet.size > 1) {
      return { disabled: true, title: "Only one Cust Code can be posted at a time." };
    }
    if (selectionMeta.hasBlankCust) {
      return { disabled: true, title: "Selected rows are missing Cust Code." };
    }
    return { disabled: false, title: "Generate iLS Outbound preview." };
  }, [selectedRecords.length, selectionMeta, ilsPreviewLoading, hasPendingEdits]);

  const fetchDataset = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${API_BASE}/routing/x-pos`, { headers: userHeaders });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        throw new Error(payload?.error || "Unable to load Routing X POs dataset.");
      }
      const shaped = ensureDatasetShape(payload.dataset);
      shaped.headers = reorderRoutingHeaders(shaped.headers);
      setDataset(shaped);
      setItemsByRoutingRowId(
        payload?.itemsByRoutingRowId && typeof payload.itemsByRoutingRowId === "object"
          ? payload.itemsByRoutingRowId
          : {}
      );
        // meta is stored in payload.meta; currently not surfaced in UI
    } catch (err) {
      setError(err?.message || "Unable to load Routing X POs dataset.");
      setDataset(EMPTY_DATASET);
      setItemsByRoutingRowId({});
    } finally {
      setLoading(false);
    }
  }, [userHeaders]);

  useEffect(() => {
    fetchDataset();
  }, [fetchDataset]);
  useEffect(() => {
    setSelectedRows((prev) => {
      if (!prev.size) return prev;
      const validIds = new Set(
        (dataset.data || [])
          .map((row) => row?.[ROW_ID_KEY])
          .filter((value) => value !== undefined && value !== null && value !== "")
      );
      const next = new Set();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [dataset.data]);
  useEffect(() => {
    if (!displayableHeaders.length) {
      setVisibleColumnKeys(new Set());
      return;
    }
    setVisibleColumnKeys((previous) => {
      const available = new Set(displayableHeaders.map((header) => header.key));
      const preserved = new Set([...previous].filter((key) => available.has(key)));
      if (preserved.size) return preserved;
      const defaults = displayableHeaders
        .filter((header) => DEFAULT_VISIBLE_LABELS.includes(header.label))
        .map((header) => header.key);
      return new Set(defaults.length ? defaults : displayableHeaders.map((header) => header.key));
    });
  }, [displayableHeaders]);
  useEffect(() => {
    if (!columnFilterOpen) return;
    const handleClickOutside = (event) => {
      if (columnFilterRef.current && !columnFilterRef.current.contains(event.target)) {
        setColumnFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [columnFilterOpen]);

  const handleToggleColumnVisibility = useCallback((key) => {
    setVisibleColumnKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size === 1) return prev;
        next.delete(key);
        return next;
      }
      next.add(key);
      return next;
    });
  }, []);

  const handleSelectDefaultColumns = useCallback(() => {
    if (!displayableHeaders.length) return;
    const defaults = displayableHeaders
      .filter((header) => DEFAULT_VISIBLE_LABELS.includes(header.label))
      .map((header) => header.key);
    setVisibleColumnKeys(
      new Set(defaults.length ? defaults : displayableHeaders.map((header) => header.key))
    );
  }, [displayableHeaders]);

  const handleSelectAllColumns = useCallback(() => {
    if (!displayableHeaders.length) return;
    setVisibleColumnKeys(new Set(displayableHeaders.map((header) => header.key)));
  }, [displayableHeaders]);

  const uniqueOptions = useMemo(() => {
    const cust = new Set();
    const rt = new Set();
    const pd = new Set();
    dataset.data.forEach((row) => {
      if (keyMap.cust) {
        const value = row[keyMap.cust];
        if (value) cust.add(String(value));
      }
      if (keyMap.rtStatus) {
        const value = row[keyMap.rtStatus];
        if (value) rt.add(String(value));
      }
      if (keyMap.pdStatus) {
        const value = row[keyMap.pdStatus];
        if (value) pd.add(String(value));
      }
    });
    const toSorted = (set) => Array.from(set).sort((a, b) => a.localeCompare(b));
    return {
      cust: toSorted(cust),
      rtStatus: toSorted(rt),
      pdStatus: toSorted(pd),
    };
  }, [dataset.data, keyMap.cust, keyMap.rtStatus, keyMap.pdStatus]);

  const filteredRows = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const matches = dataset.data.filter((row) => {
      if (filters.custCode && keyMap.cust) {
        if (String(row[keyMap.cust] || "").toUpperCase() !== filters.custCode.toUpperCase()) {
          return false;
        }
      }
      if (filters.rtStatus && keyMap.rtStatus) {
        if (String(row[keyMap.rtStatus] || "") !== filters.rtStatus) {
          return false;
        }
      }
      if (filters.pdStatus && keyMap.pdStatus) {
        if (String(row[keyMap.pdStatus] || "") !== filters.pdStatus) {
          return false;
        }
      }
      if (search) {
        const haystack = [
          row[keyMap.poNumber],
          row[keyMap.poSharp],
          row[keyMap.loadId],
          row[keyMap.ils],
        ]
          .map((value) => (value === null || value === undefined ? "" : String(value).toLowerCase()))
          .join(" ");
        if (!haystack.includes(search)) {
          return false;
        }
      }
      return true;
    });
    if (!outboundCodeKey) return matches;
    return matches
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const aBlank = !hasOutboundCode(a.row);
        const bBlank = !hasOutboundCode(b.row);
        if (aBlank !== bBlank) return aBlank ? -1 : 1;
        return a.index - b.index;
      })
      .map((entry) => entry.row);
  }, [dataset.data, filters, keyMap, outboundCodeKey, hasOutboundCode]);

  const rowCount = dataset.data.length;
  const filteredCount = filteredRows.length;

  const totalPages = useMemo(() => {
    if (!filteredCount) return 1;
    if (pageSize === "All") return 1;
    const size = Number(pageSize) || 50;
    return Math.max(1, Math.ceil(filteredCount / size));
  }, [filteredCount, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    if (pageSize === "All") return filteredRows;
    const size = Number(pageSize) || 50;
    const start = (page - 1) * size;
    return filteredRows.slice(start, start + size);
  }, [filteredRows, page, pageSize]);

  const numericPageSize = pageSize === "All" ? filteredCount : Number(pageSize) || 0;
  const startIdx = filteredCount === 0 ? 0 : pageSize === "All" ? 0 : (page - 1) * numericPageSize;
  const endIdx =
    filteredCount === 0
      ? 0
      : pageSize === "All"
        ? filteredCount
        : Math.min(filteredCount, startIdx + numericPageSize);

  const visibleRowIds = useMemo(() => {
    if (!pagedRows.length) return [];
    if (!outboundCodeKey) return pagedRows.map((row) => row[ROW_ID_KEY]);
    return pagedRows
      .filter((row) => !hasOutboundCode(row))
      .map((row) => row[ROW_ID_KEY]);
  }, [pagedRows, outboundCodeKey, hasOutboundCode]);
  const selectableRowIdSet = useMemo(() => new Set(visibleRowIds), [visibleRowIds]);
  useEffect(() => {
    setSelectedRows((prev) => {
      if (!prev.size) return prev;
      const next = new Set();
      prev.forEach((id) => {
        if (selectableRowIdSet.has(id)) next.add(id);
      });
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [selectableRowIdSet]);
  const allVisibleSelected = useMemo(
    () => visibleRowIds.length > 0 && visibleRowIds.every((id) => selectedRows.has(id)),
    [visibleRowIds, selectedRows]
  );
  const someVisibleSelected = useMemo(
    () => visibleRowIds.some((id) => selectedRows.has(id)),
    [visibleRowIds, selectedRows]
  );
  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    headerCheckboxRef.current.indeterminate = !allVisibleSelected && someVisibleSelected;
  }, [allVisibleSelected, someVisibleSelected]);

  const toggleRowSelection = useCallback(
    (id) => {
      if (!selectableRowIdSet.has(id)) return;
      setSelectedRows((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [selectableRowIdSet],
  );

  const handleToggleSelectAll = useCallback(() => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleRowIds.forEach((id) => next.delete(id));
      } else {
        visibleRowIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allVisibleSelected, visibleRowIds]);

  const handleDeleteSelected = useCallback(async () => {
    if (!dataset || selectedRows.size === 0) return;
    const selectedRecords = dataset.data.filter((row) => selectedRows.has(row[ROW_ID_KEY]));
    if (!selectedRecords.length) {
      setSelectedRows(new Set());
      return;
    }
    const confirmed = window.confirm(
      `Delete ${selectedRecords.length} selected ${selectedRecords.length === 1 ? "row" : "rows"} from this view?`
    );
    if (!confirmed) return;

    const normalize = (value) => (value === undefined || value === null ? "" : String(value).trim());
    const rowsPayload = selectedRecords
      .map((row) => ({
        custCode: normalize(row[custKey] || "").toUpperCase(),
        poNumber: normalize(row[poNumberKey] || row[poSharpKey] || ""),
        loadId: normalize(row[loadIdKey] || ""),
        routingRowId: normalize(row[ROUTING_ROW_ID_KEY] || ""),
      }))
      .filter((entry) => entry.custCode && entry.poNumber);

    if (!rowsPayload.length) {
      setError("Selected rows are missing Cust Code / PO values. Refresh the page and try again.");
      return;
    }

    setActionPending(true);
    setError("");
    try {
      const resp = await fetch(`${API_BASE}/routing/x-pos/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...userHeaders,
        },
        body: JSON.stringify({ rows: rowsPayload }),
      });
      const payload = await resp.json().catch(() => ({}));
      const serverError = typeof payload?.error === "string" ? payload.error : "";
      if (!resp.ok || payload?.ok !== true) {
        const friendlyMessage =
          serverError === "rows_not_found"
            ? "Selected rows were not found. Refresh the table and try again."
          : serverError === "dataset_missing"
            ? "Routing X POs dataset is unavailable. Run Allocate/Refresh before deleting."
          : serverError === "read_only_role"
            ? "Your role does not allow deleting Routing X POs rows."
          : serverError === "rows_invalid" || serverError === "rows_required"
            ? "Select at least one row that includes Cust Code and PO data."
            : serverError === "outbound_locked"
              ? "Rows already posted to iLS Outbound cannot be deleted."
            : "";
        throw new Error(friendlyMessage || serverError || "Unable to delete selected rows.");
      }
      if (payload.dataset) {
        const shaped = ensureDatasetShape(payload.dataset);
        shaped.headers = reorderRoutingHeaders(shaped.headers);
        setDataset(shaped);
        setItemsByRoutingRowId(
          payload?.itemsByRoutingRowId && typeof payload.itemsByRoutingRowId === "object"
            ? payload.itemsByRoutingRowId
            : {}
        );
      } else {
        await fetchDataset();
      }
        // metadata updates are currently unused on this screen
      setSelectedRows(new Set());
    } catch (err) {
      setError(err?.message || "Unable to delete selected rows.");
    } finally {
      setActionPending(false);
    }
  }, [
    dataset,
    selectedRows,
    custKey,
    poNumberKey,
    poSharpKey,
    loadIdKey,
    userHeaders,
    fetchDataset,
  ]);
  const handleAllocChange = useCallback(
    (row, sku, rawValue) => {
      if (!canEditAlloc) return;
      const nextValue = rawValue === "" ? "" : rawValue.replace(/\D+/g, "");
      if (rawValue !== "" && nextValue === "") {
          return;
      }
      const normalized = nextValue;
      const rowKey = row[ROW_ID_KEY];
      if (!rowKey) return;
      const rowItems = getItemsForRow(row);
      const originalItem = rowItems.find(
        (item) => (item?.sku || "").toString().trim() === (sku || "").toString().trim()
      );
      setPendingEdits((prev) => {
        const next = { ...prev };
        const currentEntry = { ...(next[rowKey] || {}) };
        const numericValue = normalized === "" ? 0 : parseInt(normalized, 10);
        const originalValue = parseAllocValue(originalItem?.allocQty ?? 0);
        if (numericValue === originalValue) {
          delete currentEntry[sku];
        } else {
          currentEntry[sku] = normalized;
        }
        if (Object.keys(currentEntry).length === 0) {
          delete next[rowKey];
        } else {
          next[rowKey] = currentEntry;
        }
        return Object.keys(next).length ? next : {};
      });
    },
    [canEditAlloc, getItemsForRow, parseAllocValue]
  );

  const handleGenerateIlsPreview = useCallback(async () => {
    if (!custKey || (!poNumberKey && !poSharpKey) || !loadIdKey) {
      setError("Routing dataset is missing required columns. Refresh and try again.");
      return;
    }
    if (!selectedRecords.length) {
      setError("Select at least one Routing row before posting iLS Outbound.");
      return;
    }
    if (hasPendingEdits) {
      setError("Please save allocation changes before posting iLS Outbound.");
      return;
    }
    if (selectionMeta.hasMismatch) {
      setError("Remove rows flagged as PO mismatch before posting iLS Outbound.");
      return;
    }
    if (selectionMeta.custSet.size > 1) {
      setError("Only one Cust Code can be posted each time.");
      return;
    }
    if (selectionMeta.hasBlankCust) {
      setError("Selected rows are missing Cust Code values.");
      return;
    }
    const normalize = (value) => (value === undefined || value === null ? "" : String(value).trim());
    const rowsPayload = selectedRecords
      .map((row) => ({
        custCode: normalize(row[custKey]).toUpperCase(),
        poNumber: normalize(row[poNumberKey] || ""),
        poSharp: normalize(row[poSharpKey] || ""),
        loadId: normalize(row[loadIdKey] || ""),
        routingRowId: normalize(row[ROUTING_ROW_ID_KEY] || ""),
      }))
      .filter((entry) => entry.custCode && (entry.poNumber || entry.poSharp));
    if (!rowsPayload.length) {
      setError("Selected rows are missing Cust Code / PO values. Refresh the page and try again.");
      return;
    }
    setIlsPreviewLoading(true);
    setError("");
    setIlsPostMessage("");
    setIlsPostSummary(null);
    try {
      const resp = await fetch(`${API_BASE}/routing/x-pos/ils-ob/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...userHeaders,
        },
        body: JSON.stringify({ rows: rowsPayload }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        throw new Error(formatIlsObError(payload?.error, payload) || payload?.message || "Unable to build iLS Outbound preview.");
      }
      setIlsPreview({
        rows: Array.isArray(payload.rows) ? payload.rows : [],
        columns: Array.isArray(payload.columns) ? payload.columns : [],
        custCode: payload.custCode || "",
        summary: payload.summary || { perWarehouse: [], totalOrders: 0 },
      });
      setIlsModalOpen(true);
    } catch (err) {
      setError(err?.message || "Unable to build iLS Outbound preview.");
    } finally {
      setIlsPreviewLoading(false);
    }
  }, [
    custKey,
    poNumberKey,
    poSharpKey,
    loadIdKey,
    selectedRecords,
    selectionMeta,
    hasPendingEdits,
    userHeaders,
  ]);
  const handlePostIlsOutbound = useCallback(
    async (options = {}) => {
      if (!ilsPreview || !Array.isArray(ilsPreview.rows) || !ilsPreview.rows.length) {
        setIlsPostMessage("Preview data is missing. Generate the preview again.");
        return;
      }
      const retryFailed = Boolean(options.retryFailed);
      let rowsToPost = ilsPreview.rows;
      if (retryFailed && ilsPostSummary) {
        const failedOrders = new Set(
          (ilsPostSummary.rows || [])
            .filter((row) => !row.outboundCode)
            .map((row) => (row.customerOrder || "").trim())
            .filter(Boolean)
        );
        rowsToPost = rowsToPost.filter((row) => failedOrders.has((row.customerOrder || "").trim()));
        if (!rowsToPost.length) {
          setIlsPostMessage("No failed customer orders to retry.");
          return;
        }
      }
      setPostingIls(true);
      setIlsPostMessage("");
      try {
        const resp = await fetch(`${API_BASE}/routing/x-pos/ils-ob/post`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...userHeaders,
          },
          body: JSON.stringify({
            rows: rowsToPost,
            custCode: ilsPreview.custCode || "",
          }),
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(formatIlsPostError(payload?.error, payload) || payload?.message || "Unable to post iLS Outbound.");
        }
        if (payload.dataset) {
          const shaped = ensureDatasetShape(payload.dataset);
          shaped.headers = reorderRoutingHeaders(shaped.headers);
          setDataset(shaped);
          setItemsByRoutingRowId(
            payload?.itemsByRoutingRowId && typeof payload.itemsByRoutingRowId === "object"
              ? payload.itemsByRoutingRowId
              : {}
          );
        } else if (payload.updatedAt) {
          await fetchDataset();
        }
        setIlsPostSummary({
          success: payload.success || 0,
          failed: payload.failed || 0,
          rows: Array.isArray(payload.summary) ? payload.summary : [],
        });
        if (payload.message) {
          setIlsPostMessage(payload.message);
        } else if ((payload.failed || 0) === 0) {
          setIlsPostMessage("All outbound requests were created successfully.");
        } else {
          setIlsPostMessage("Some outbound requests failed. Review the summary for details.");
        }
      } catch (err) {
        setIlsPostMessage(err?.message || "Unable to post iLS Outbound.");
      } finally {
        setPostingIls(false);
      }
    },
    [fetchDataset, ilsPostSummary, ilsPreview, userHeaders]
  );
  const handleSaveEdits = useCallback(async () => {
    if (!canEditAlloc || !hasPendingEdits || allocationStats.hasBlockingMismatch) {
      return false;
    }
    const rowsPayload = Object.entries(pendingEdits)
      .map(([rowKey, overrides]) => {
        const row = datasetRowMap.get(rowKey);
        if (!row || !overrides || Object.keys(overrides).length === 0) return null;
        const allocs = {};
        Object.entries(overrides).forEach(([sku, value]) => {
          allocs[sku] = parseAllocValue(value);
        });
        if (!Object.keys(allocs).length) return null;
        return {
          routingRowId: row[ROUTING_ROW_ID_KEY] || "",
          custCode: (row[custKey] || "").toString(),
          poNumber: (row[poNumberKey] || row[poSharpKey] || "").toString(),
          loadId: (row[loadIdKey] || "").toString(),
          allocs,
        };
      })
      .filter(Boolean);
    if (!rowsPayload.length) {
      setPendingEdits({});
      return false;
    }
    setSavingEdits(true);
    setError("");
    try {
      const resp = await fetch(`${API_BASE}/routing/x-pos/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...userHeaders,
        },
        body: JSON.stringify({ rows: rowsPayload }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        const friendly = formatAllocSaveError(payload?.error, payload);
        throw new Error(friendly || payload?.message || "Unable to save allocations.");
      }
      if (payload.dataset) {
        const shaped = ensureDatasetShape(payload.dataset);
        shaped.headers = reorderRoutingHeaders(shaped.headers);
        setDataset(shaped);
        setItemsByRoutingRowId(
          payload?.itemsByRoutingRowId && typeof payload.itemsByRoutingRowId === "object"
            ? payload.itemsByRoutingRowId
            : {}
        );
      } else {
        await fetchDataset();
      }
      setPendingEdits({});
      return true;
    } catch (err) {
      setError(err?.message || "Unable to save allocations.");
      return false;
    } finally {
      setSavingEdits(false);
    }
  }, [
    allocationStats.hasBlockingMismatch,
    canEditAlloc,
    custKey,
    datasetRowMap,
    fetchDataset,
    hasPendingEdits,
    loadIdKey,
    pendingEdits,
    poNumberKey,
    poSharpKey,
    parseAllocValue,
    userHeaders,
  ]);
  const handleCloseAllocEditor = useCallback(() => {
    const rowKeys = allocationEditorEntries
      .map((entry) => entry?.rowKey)
      .filter((value) => value !== undefined && value !== null && value !== "");
    if (rowKeys.length) {
      const dropSet = new Set(rowKeys);
      setPendingEdits((prev) => {
        if (!prev || typeof prev !== "object") return {};
        const next = { ...prev };
        let changed = false;
        rowKeys.forEach((rowKey) => {
          if (Object.prototype.hasOwnProperty.call(next, rowKey)) {
            delete next[rowKey];
            changed = true;
          }
        });
        if (!changed) return prev;
        return Object.fromEntries(
          Object.entries(next).filter(([rowKey]) => !dropSet.has(rowKey))
        );
      });
    }
    setAllocEditorPoKey("");
  }, [allocationEditorEntries]);

  const exportToExcel = useCallback(() => {
    if (!filteredRows.length) return;
    const exportHeaders = orderedVisibleHeaders;
    const headerRow = exportHeaders.map((header) => header.label || header.key);
    const dataRows = filteredRows.map((row) =>
      exportHeaders.map((header) => row[header.key] ?? "")
    );
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Routing X POs");
    const timestamp = new Date().toISOString().replace(/[:T-]/g, "").slice(0, 14);
    XLSX.writeFile(workbook, `Routing_X_POs_${timestamp}.xlsx`);
  }, [orderedVisibleHeaders, filteredRows]);

const renderValue = (header, value, keyMap) => {
  if (header.type === "number") {
    return formatNumber(value);
  }
  if (header.key === keyMap.totalCases) {
    return formatNumber(value);
  }
  if (shouldFormatAsDate(header)) {
    return formatDate(value, header.key);
  }
  return value ?? "";
};

  const isRowOverAllocated = (row) => {
    if (!keyMap.rowAllocSum || !keyMap.cases) return false;
    const alloc = Number(row[keyMap.rowAllocSum] ?? 0);
    const cases = Number(row[keyMap.cases] ?? 0);
    return alloc > cases;
  };

  const headerClass = (header, isSticky) =>
    [
      "sticky top-0 z-10 px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase border-b border-slate-200",
      SOURCE_HEADER_CLASS[header.source] || "bg-slate-50",
      isSticky ? "shadow-sm bg-white" : "",
    ]
      .filter(Boolean)
      .join(" ");

  const cellClass = (header, row, isSticky, highlight = false) => {
    const base = "px-3 py-1 text-sm text-slate-700 border-b border-slate-100 whitespace-nowrap";
    if (highlight) {
      return `${base} text-red-600 font-semibold`;
    }
    if (header.key === keyMap.routingVsPo && Number(row[keyMap.routingVsPo] || 0) !== 0) {
      return `${base} text-amber-700 font-semibold`;
    }
    if (header.key === keyMap.rowAllocSum && isRowOverAllocated(row)) {
      return `${base} text-red-700 font-semibold`;
    }
    return isSticky ? `${base} sticky bg-inherit` : base;
  };

  return (
    <>
      <div className="px-4 py-6 space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">Routing X POs</h1>
            <p className="text-sm text-slate-500">
              Allocate POs to Routing for creating OB orders.
            </p>
            <UserAccessSummary className="mt-2 text-xs text-slate-500" />
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={!filteredRows.length || loading}
              onClick={exportToExcel}
              className="inline-flex items-center rounded-md border border-emerald-600 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            >
              <Download className="mr-2 h-4 w-4" />
              Export
            </button>
            <button
              type="button"
              disabled={ilsButtonInfo.disabled}
              onClick={handleGenerateIlsPreview}
            className="inline-flex items-center rounded-md border border-blue-500 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
            title={ilsButtonInfo.title}
            >
              {ilsPreviewLoading ? "Preparing..." : "Post iLS OB"}
            </button>
            <button
              type="button"
              disabled={disableSaveAllocations}
              onClick={handleSaveEdits}
              className="inline-flex items-center rounded-md border border-indigo-500 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
              title={saveButtonTitle}
            >
              {savingEdits ? "Saving..." : "Save Allocations"}
            </button>
            <button
              type="button"
              disabled={!selectedRows.size || actionPending}
              onClick={handleDeleteSelected}
              className="inline-flex items-center rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            title={
              actionPending
                ? "Deleting selected rows..."
                : selectedRows.size
                  ? `Delete ${selectedRows.size} selected rows`
                  : "Select rows to delete"
            }
          >
              {actionPending ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <p className="font-semibold">PO quantity comparison</p>
          <p className="mt-1">
            TTL PO QTY is the original PO total. Eligible PO QTY is the current non-cancelled line total used to validate allocations.
          </p>
        </div>
        {(dataset.checks?.eligibleQtyDiagnostics || []).length > 0 && (
          <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
              <div>
                <p className="font-semibold">Eligible PO QTY is unavailable for one or more POs.</p>
                <p className="mt-1">Allocation changes are blocked only for the affected Cust Code and PO.</p>
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  {dataset.checks.eligibleQtyDiagnostics.slice(0, 5).map((item, index) => (
                    <li key={`${item?.custCode || "unknown"}|${item?.poNumber || "unknown"}|${index}`}>
                      {item?.custCode || "Unknown customer"} / {item?.poNumber || "Unknown PO"}: {item?.message || "Eligible PO QTY could not be determined."}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-600">
          <ListFilter className="h-4 w-4" />
          Filters
        </div>
        <div className="grid gap-3 md:grid-cols-6 items-end">
          <label className="text-sm text-slate-600 md:col-span-2">
            Search (PO / Load / iLS)
            <input
              type="text"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
              placeholder="e.g. 673934000"
            />
          </label>
          <SelectField
            label="Cust Code"
            value={filters.custCode}
            onChange={(value) => updateFilter("custCode", value)}
            options={uniqueOptions.cust}
            className="md:w-44"
          />
          <SelectField
            label="RT Status"
            value={filters.rtStatus}
            onChange={(value) => updateFilter("rtStatus", value)}
            options={uniqueOptions.rtStatus}
            className="md:w-44"
          />
          <SelectField
            label="PD Status"
            value={filters.pdStatus}
            onChange={(value) => updateFilter("pdStatus", value)}
            options={uniqueOptions.pdStatus}
            className="md:w-44"
          />
          <div className="relative text-sm text-slate-600">
            <span>Columns</span>
            <button
              type="button"
              disabled={!displayableHeaders.length}
              onClick={() => setColumnFilterOpen((prev) => !prev)}
              className="mt-1 flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span>
                {visibleColumnKeys.size}/{displayableHeaders.length || 0}
              </span>
              <svg className="h-4 w-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 011.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            {columnFilterOpen && (
              <div
                ref={columnFilterRef}
                className="absolute right-0 z-20 mt-2 w-72 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700 shadow-lg"
              >
                <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                  <button type="button" className="hover:text-slate-700" onClick={handleSelectDefaultColumns}>
                    Defaults
                  </button>
                  <button type="button" className="hover:text-slate-700" onClick={handleSelectAllColumns}>
                    Show All
                  </button>
                </div>
                <div className="max-h-32 overflow-y-auto">
                  {displayableHeaders.map((header) => (
                    <label key={header.key} className="flex items-center gap-2 py-1">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        checked={visibleColumnKeys.has(header.key)}
                        onChange={() => handleToggleColumnVisibility(header.key)}
                      />
                      <span>{header.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
          <span>
            Showing {filteredCount.toLocaleString()} of {rowCount.toLocaleString()} rows.
          </span>
          <button
            type="button"
            onClick={handleResetFilters}
            className="text-blue-600 hover:underline"
          >
            Reset filters
          </button>
        </div>
      </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <p className="font-medium">Error</p>
            <p>{error}</p>
          </div>
        )}
        {hasAnyAllocationMismatch && (
          <p className="text-sm text-amber-600">
            Rows highlighted in red indicate TTL ALLOC totals that do not match the PO&apos;s Eligible PO QTY.
          </p>
        )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <AlertCircle className="h-4 w-4 animate-spin" />
          Loading Routing X POsÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
          {dataset.data.length === 0
            ? "No data yet. Run PO Allocate from Routing Status to populate this table."
            : "No rows match the current filters."}
        </div>
      ) : (
        <>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="max-h-[70vh] overflow-x-auto overflow-y-auto">
                <table className="min-w-full text-left text-sm">
            <thead>
              <tr>
                <th
                  className="sticky top-0 left-0 z-20 bg-white px-3 py-2 text-xs font-semibold uppercase text-slate-600 border-b border-slate-200"
                  style={{ width: selectionColumnWidth, minWidth: selectionColumnWidth, top: 0 }}
                >
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    onChange={handleToggleSelectAll}
                    checked={allVisibleSelected && visibleRowIds.length > 0}
                    disabled={!visibleRowIds.length}
                    title={!visibleRowIds.length ? "No selectable rows on this page" : ""}
                  />
                </th>
                      {orderedVisibleHeaders.map((header) => {
                  const stickyInfo = stickyMeta[header.key];
                  const isSticky = Boolean(stickyInfo);
                  const style = isSticky
                    ? {
                        left: stickyInfo.left,
                        minWidth: stickyInfo.width,
                        width: stickyInfo.width,
                      }
                    : undefined;
                  return (
                    <th key={header.key} className={headerClass(header, isSticky)} style={style}>
                      {header.label}
                    </th>
                  );
                })}
                <th className="sticky top-0 z-10 bg-slate-50 px-3 py-2 text-left text-xs font-semibold uppercase text-slate-600 border-b border-slate-200">
                  Allocations
                </th>
              </tr>
            </thead>
                <tbody>
                  {pagedRows.map((row, idx) => {
                    const hasPoMatch = row[PO_MISMATCH_FLAG_KEY] !== false;
                    const rowKey = row[ROW_ID_KEY];
                    const poKey = rowKey ? allocationStats.rowToPo.get(rowKey) : null;
                    const rowSumOverride = rowKey ? allocationStats.rowSums.get(rowKey) : undefined;
                    const totalsMismatch =
                      rowKey &&
                      (allocationStats.rowMismatch.has(rowKey) ||
                        (poKey && allocationStats.poMismatch.has(poKey)));
                    const eligibleQtyUnavailable = Boolean(
                      poKey && allocationStats.unavailablePoKeys.has(poKey)
                    );
                    const rowPosted = hasOutboundCode(row);
                    let rowClassName = "";
                    let rowTitle;
                    if (!hasPoMatch) {
                      rowClassName = "bg-red-100";
                      rowTitle = "No matching PO Details row for this Cust Code + PO.";
                    } else if (eligibleQtyUnavailable) {
                      rowClassName = "bg-amber-100";
                      rowTitle =
                        allocationStats.poDiagnostics.get(poKey) ||
                        row[ELIGIBLE_PO_QTY_DIAGNOSTIC_KEY] ||
                        "Eligible PO QTY is unavailable for this Cust Code and PO.";
                    } else if (totalsMismatch) {
                      rowClassName = "bg-red-100";
                      rowTitle = "TTL ALLOC totals do not match this PO's Eligible PO QTY.";
                    } else if (isRowOverAllocated(row)) {
                      rowClassName = "bg-red-50";
                    } else {
                      rowClassName = idx % 2 === 0 ? "bg-white" : "bg-slate-50";
                    }
                    return (
                      <tr key={`row-${idx}`} className={rowClassName} title={rowTitle}>
                        <td
                          className="sticky left-0 z-10 bg-inherit px-3 py-2 border-b border-slate-200"
                          style={{ width: selectionColumnWidth, minWidth: selectionColumnWidth }}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            checked={selectedRows.has(row[ROW_ID_KEY])}
                            onChange={() => toggleRowSelection(row[ROW_ID_KEY])}
                            disabled={rowPosted}
                            title={
                              rowPosted
                                ? "Outbound code already assigned. Posted rows cannot be selected."
                                : ""
                            }
                          />
                        </td>
                        {orderedVisibleHeaders.map((header) => {
                          const stickyInfo = stickyMeta[header.key];
                          const isSticky = Boolean(stickyInfo);
                          const style = isSticky
                            ? {
                                left: stickyInfo.left,
                                minWidth: stickyInfo.width,
                                width: stickyInfo.width,
                              }
                            : undefined;
                          const highlightTotals =
                            (totalsMismatch || eligibleQtyUnavailable) &&
                            (header.key === keyMap.rowAllocSum || header.key === keyMap.eligiblePerPo);
                          let displayValue = row[header.key];
                          if (header.key === keyMap.eligiblePerPo && eligibleQtyUnavailable) {
                            displayValue = "Unavailable";
                          } else if (header.key === keyMap.rowAllocSum && rowSumOverride !== undefined) {
                            displayValue = rowSumOverride;
                          } else if (header.key === keyMap.routingVsPo) {
                            const casesVal = parseAllocValue(row[keyMap.cases]);
                            const allocVal =
                              rowSumOverride !== undefined
                                ? rowSumOverride
                                : parseAllocValue(row[rowAllocSumKey]);
                            displayValue = casesVal - allocVal;
                          }
                          return (
                            <td
                              key={`${header.key}-${idx}`}
                              className={cellClass(header, row, isSticky, highlightTotals)}
                              style={style}
                            >
                              {renderValue(header, displayValue, keyMap)}
                            </td>
                          );
                        })}
                        <td className="px-3 py-1 border-b border-slate-100">
                          <button
                            type="button"
                            className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                            onClick={() => {
                              if (poKey) setAllocEditorPoKey(poKey);
                            }}
                            disabled={!poKey}
                          >
                            View/Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                </table>
              </div>
            </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(event) => {
                const value = event.target.value === "All" ? "All" : parseInt(event.target.value, 10);
                setPageSize(value);
                setPage(1);
              }}
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span className="tabular-nums text-xs text-slate-500">
              {filteredCount === 0 ? 0 : startIdx + 1} - {endIdx} of {filteredCount}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setPage(1)}
                disabled={page <= 1}
                aria-label="First page"
              >
                <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <span className="tabular-nums">
                {totalPages === 0 ? 0 : page} / {totalPages}
              </span>
              <button
                type="button"
                className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                aria-label="Last page"
              >
                <ChevronsRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
          </div>
        </>
      )}
      </div>
      {allocationEditorEntries.length > 0 && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/35" onClick={handleCloseAllocEditor} />
          <div className="relative z-10 w-full max-w-4xl rounded-xl bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-slate-800">Allocations</h2>
              <p className="mt-1 text-xs text-slate-500">
                PO {allocationEditorPoLabel || "(blank)"} · {allocationEditorEntries.length} load
                {allocationEditorEntries.length > 1 ? "s" : ""}
              </p>
            </div>
            <div className="max-h-[60vh] space-y-4 overflow-auto px-5 py-4">
              {allocationEditorEntries.map((entry, entryIndex) => (
                <div key={entry.rowKey || `alloc-entry-${entryIndex}`} className="overflow-hidden rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-sm font-medium text-slate-700">
                      Load {entry.loadId || "(blank)"}
                    </p>
                    {entry.rowPosted && (
                      <span className="rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                        Posted / Read-only
                      </span>
                    )}
                  </div>
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">SKU</th>
                        <th className="px-3 py-2 text-right font-semibold">PO Qty</th>
                        <th className="px-3 py-2 text-right font-semibold">Alloc Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entry.items.map((item, itemIndex) => (
                        <tr key={`${entry.rowKey || entryIndex}-${item.sku}-${itemIndex}`} className={itemIndex % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                          <td className="px-3 py-2">{item.sku}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{item.poQty.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right">
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              className="w-28 rounded border border-slate-300 px-2 py-1 text-right text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
                              value={item.inputValue}
                              disabled={entry.rowPosted || !canEditAlloc}
                              onChange={(event) => handleAllocChange(entry.row, item.sku, event.target.value)}
                            />
                          </td>
                        </tr>
                      ))}
                      {!entry.items.length && (
                        <tr>
                          <td colSpan={3} className="px-3 py-4 text-center text-slate-500">
                            No allocation rows for this load.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
                <p className="text-xs text-slate-500">
                  {!canEditAlloc
                    ? "Your role is read-only for allocations."
                    : !allocationEditorHasEditableRows
                    ? "All loads under this PO are posted and read-only."
                    : "Edit one or multiple loads under this PO, then save once."}
                </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded border border-indigo-500 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={handleSaveEdits}
                  disabled={disableSaveAllocations}
                  title={saveButtonTitle}
                >
                  {savingEdits ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  onClick={handleCloseAllocEditor}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {ilsModalOpen && ilsPreview && (
        <IlsPreviewModal
          data={ilsPreview}
          message={ilsPostMessage}
          posting={postingIls}
          postSummary={ilsPostSummary}
          onClose={() => {
            setIlsModalOpen(false);
            setIlsPostMessage("");
            setIlsPostSummary(null);
            setPostingIls(false);
          }}
          onPostNow={() => handlePostIlsOutbound()}
          onRetryFailed={() => handlePostIlsOutbound({ retryFailed: true })}
        />
      )}
    </>
  );
}


function SelectField({ label, value, onChange, options, className = "" }) {
  return (
    <label className={`text-sm text-slate-600 ${className}`}>
      {label}
      <select
        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">All</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

const PAGE_OPTIONS = [25, 50, 100, "All"];

function IlsPreviewModal({
  data,
  onClose,
  onPostNow,
  onRetryFailed,
  message,
  posting = false,
  postSummary = null,
}) {
  if (!data) return null;
  const columns = Array.isArray(data.columns) ? data.columns : [];
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const summary = data.summary || {};
  const perWarehouse = Array.isArray(summary.perWarehouse) ? summary.perWarehouse : [];
  const totalOrders = summary.totalOrders || 0;
  const custCode = data.custCode || "";
  const summaryRows = postSummary?.rows || [];
  const successCount = postSummary?.success || 0;
  const failedCount = postSummary?.failed || 0;
  const renderValue = (value) => {
    if (value === true) return "TRUE";
    if (value === false) return "FALSE";
    if (value === null || value === undefined) return "";
    return String(value);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {posting && (
          <div className="absolute inset-x-0 top-0 h-1 overflow-hidden rounded-t-2xl bg-emerald-100">
            <div className="h-full w-full animate-pulse bg-emerald-500" />
          </div>
        )}
        <div className="flex items-center justify-between px-6 pt-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Post iLS Outbound Preview</h2>
            <p className="mt-1 text-sm text-slate-600">
              You're one step to create iLS Outbound!
              <br />
              Please verify below information for {custCode || "selected Cust Code"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const headerRow = columns.map((col) => col.label || col.key);
              const dataRows = rows.map((row) => columns.map((col) => row[col.key] ?? ""));
              const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
              const workbook = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(workbook, worksheet, "Outbound");
              const timestamp = new Date().toISOString().replace(/[:T-]/g, "").slice(0, 14);
              const safeCust = custCode ? custCode.replace(/[^a-z0-9_-]/gi, "_") : "outbound";
              XLSX.writeFile(workbook, `${safeCust}_iLS_Outbound_${timestamp}.xlsx`);
            }}
            className="rounded border border-emerald-500 px-4 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-50"
          >
            Export
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <div>
            <ul className="mt-3 space-y-1 text-sm text-slate-700">
              {perWarehouse.length ? (
                perWarehouse.map((item, idx) => (
                  <li key={`${item.warehouseCode || "UNKNOWN"}-${idx}`}>
                    {(item.warehouseCode || "UNKNOWN").toUpperCase()} : {item.orderCount ?? 0}
                  </li>
                ))
              ) : (
                <li>No warehouse grouping available.</li>
              )}
              <li className="pt-1 font-semibold text-slate-900">Total {totalOrders || 0} outbound</li>
            </ul>
            {message && (
              <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {message}
              </div>
            )}
            {posting && (
              <div className="mt-3 flex items-center gap-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                <span className="h-2 w-2 animate-ping rounded-full bg-emerald-500" />
                Posting to iLS... please wait.
              </div>
            )}
          </div>
          {postSummary && (
            <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <p className="font-semibold">Success: {successCount} • Failed: {failedCount}</p>
              {summaryRows.length ? (
                <div className="mt-2 max-h-40 overflow-y-auto rounded border border-slate-200 bg-white">
                  <table className="min-w-full text-left text-xs">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Customer Order</th>
                        <th className="px-3 py-2 font-semibold">Status</th>
                        <th className="px-3 py-2 font-semibold">Outbound Code</th>
                        <th className="px-3 py-2 font-semibold">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryRows.map((item, idx) => (
                        <tr key={`ils-summary-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                          <td className="px-3 py-1">{item.customerOrder || ""}</td>
                          <td className="px-3 py-1">{item.status || ""}</td>
                          <td className="px-3 py-1">{item.outboundCode || ""}</td>
                          <td className="px-3 py-1">{item.message || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-500">No summary available yet.</p>
              )}
            </div>
          )}
          <div className="mt-4 overflow-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  {columns.map((col) => (
                    <th key={col.key || col.label} className="px-3 py-2 font-semibold uppercase tracking-wide">
                      {col.label || col.key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={`ils-preview-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                    {columns.map((col) => (
                      <td key={`${col.key || col.label}-${idx}`} className="px-3 py-1 text-slate-700">
                        {renderValue(row[col.key])}
                      </td>
                    ))}
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={columns.length || 1} className="px-3 py-4 text-center text-slate-500">
                      No preview rows available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          {postSummary ? (
            <>
              <button
                type="button"
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                onClick={onClose}
              >
                Exit
              </button>
              <button
                type="button"
                className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={onRetryFailed}
                disabled={posting || !postSummary.failed}
              >
                {posting ? "Posting..." : "Try Again"}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={onPostNow}
                disabled={posting || Boolean(postSummary)}
              >
                {posting ? "Posting..." : "Post Now"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function formatIlsObError(code, payload) {
  if (!code) return payload?.message || "";
  const missingSkus = Array.isArray(payload?.missingSkus) ? payload.missingSkus : [];
  const missingShipPoints = Array.isArray(payload?.missingShipPoints) ? payload.missingShipPoints : [];
  switch (code) {
    case "rows_required":
      return "Select at least one Routing row before posting.";
    case "rows_not_found":
      return "Some selected rows were not found. Refresh the table and try again.";
    case "po_mismatch_present":
      return "Remove rows that are highlighted as PO mismatch before posting.";
    case "multiple_cust_codes":
      return "Only one Cust Code can be posted each time.";
    case "cust_missing":
      return "Selected rows are missing Cust Code values.";
    case "cust_not_found":
      return payload?.message || "Cust Code is not defined under Admin > Customers.";
    case "product_mapping_missing":
      if (missingSkus.length) {
        return `Missing Product Code mapping for SKUs: ${missingSkus.join(", ")}.`;
      }
      return payload?.message || "Missing Product Code mapping for the selected SKUs.";
    case "warehouse_mapping_missing":
      if (missingShipPoints.length) {
        return `Ship Point missing warehouse mapping: ${missingShipPoints.join(", ")}.`;
      }
      return payload?.message || "One or more Ship Points are missing warehouse mapping.";
    case "mrp_load_failed":
      return "Unable to load MRP product mappings. Please retry later.";
    case "warehouse_load_failed":
      return "Unable to load warehouse mappings. Please retry later.";
    case "no_allocations":
      return "All selected rows have zero Alloc Qty.";
    case "dataset_structure_invalid":
    case "dataset_missing":
      return payload?.message || "Routing X POs dataset is unavailable. Run PO Allocate first.";
    default:
      return payload?.message || "Unable to build iLS Outbound preview.";
  }
}

function formatIlsPostError(code, payload) {
  if (!code) return payload?.message || "";
  switch (code) {
    case "rows_required":
      return "Preview rows are missing. Re-run Generate Preview.";
    case "ils_credentials_missing":
      return payload?.message || "iLS Client ID/Secret are missing under Admin > Customers.";
    case "ils_token_failed":
      return payload?.message || "Unable to obtain iLS access token.";
    case "preview_invalid":
      return payload?.message || "Preview rows are invalid.";
    default:
      return payload?.message || "";
  }
}

function formatAllocSaveError(code, payload) {
  if (!code) return payload?.message || "";
  switch (code) {
    case "po_allocation_mismatch":
      return "Allocation totals must equal Eligible PO QTY before saving.";
    case "eligible_po_qty_unavailable":
      return (
        payload?.poDiagnostics?.[0]?.message ||
        "Eligible PO QTY is unavailable for the affected Cust Code and PO. Allocation changes were not saved."
      );
    case "invalid_allocation_item": {
      const violation = Array.isArray(payload?.itemViolations) ? payload.itemViolations[0] : null;
      const item = violation?.item ? String(violation.item) : "";
      if (violation?.code === "ITEM_ALLOCATION_EXCEEDS_ELIGIBLE_QTY" && item) {
        return `Allocation for Walmart item ${item} exceeds its current eligible PO quantity.`;
      }
      if (item) {
        return `Walmart item ${item} is not eligible for a positive allocation on this customer and PO.`;
      }
      return payload?.message || "One or more positive allocations are not valid for the current eligible PO items.";
    }
    case "rows_required":
      return "Select at least one row to update allocations.";
    case "no_changes":
      return "No allocation changes detected.";
    case "outbound_locked":
      return "Rows already posted to iLS Outbound cannot be edited.";
    default:
      return payload?.message || "";
  }
}

